<template>
  <div class="modal-overlay">
    <div class="modal-content">
      <div
        class="header-controls d-flex justify-content-between align-items-center mb-3"
      >
        <span class="popup-title">{{ title }}</span>
        <div class="header-controls">
          <!-- <button @click="toggleTheme" class="btn-sm" title="Toggle Theme">
  <i class="fas fa-adjust"></i>
</button> -->

          <button
            @click="downloadDiagram"
            class="btn-sm"
            title="Download Diagram"
          >
            <i class="fas fa-cloud-download"></i>
          </button>
          <button @click="zoomIn" class="btn-sm" title="Zoom In">
            <i class="fas fa-plus"></i>
          </button>
          <button @click="zoomOut" class="btn-sm" title="Zoom Out">
            <i class="fas fa-minus"></i>
          </button>
          <button @click="resetZoom" class="btn-sm" title="Reset Zoom">
            <i class="fas fa-undo"></i>
          </button>
          <button @click="close" class="btn-sm" title="Close">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
      <hr class="custom-hr" />
<div
  ref="mermaidContainer"
  class="mermaid"
  :class="{ lightTheme: !isDarkTheme }"
  @wheel="handleZoom"
  @mousedown="startPan"
  @mousemove="pan"
  @mouseup="stopPan"
  @mouseleave="stopPan"
></div>

    </div>
  </div>
</template>

<script>
import { nextTick } from "vue";
import mermaid from "mermaid";

export default {
  name: "MermaidChart",
  props: ["data", "title"], // Add title as a prop
  data() {
    return {
    zoomLevel: 1,
    isDarkTheme: true
    };
  },
  mounted() {
    // Initialize Mermaid once when the component is mounted
    mermaid.initialize({ startOnLoad: false });
    this.renderMermaidChart();
  },
  watch: {
    data: "renderMermaidChart",
  },
  methods: {
    async renderMermaidChart() {
      if (typeof this.data !== "string" || !this.data.trim()) {
        console.error("Invalid Mermaid diagram data:", this.data);
        this.$refs.mermaidContainer.innerHTML = "<p>Invalid diagram data</p>";
        return;
      }

      await nextTick(); // Ensure the DOM is updated before rendering

      const container = this.$refs.mermaidContainer;
      if (!container) {
        console.error("Mermaid container is not defined.");
        return;
      }

      try {
        // Clear the container before rendering
        container.innerHTML = "";

        // Render the Mermaid diagram
        const { svg } = await mermaid.render("mermaidGraph", this.data);
        container.innerHTML = svg;

        // Apply the current zoom level to the SVG content
        this.applyZoom();
      } catch (error) {
        container.innerHTML = "<p>Failed to render diagram</p>";
      }
    },
    toggleTheme() {
  this.isDarkTheme = !this.isDarkTheme;
}
,
    handleZoom(event) {
      if (!event.ctrlKey) return; // normal scroll, no zoom

      event.preventDefault();
      const delta = event.deltaY > 0 ? -0.1 : 0.1;

      this.zoomLevel = Math.max(this.zoomLevel + delta, 0.5);
      this.applyZoom();
    },
    startPan(event) {
      this.isPanning = true;
      this.startX = event.clientX;
      this.startY = event.clientY;

      const container = this.$refs.mermaidContainer;
      this.scrollLeft = container.scrollLeft;
      this.scrollTop = container.scrollTop;

      container.style.cursor = "grabbing";
    },

    pan(event) {
      if (!this.isPanning) return;

      const container = this.$refs.mermaidContainer;
      const dx = event.clientX - this.startX;
      const dy = event.clientY - this.startY;

      container.scrollLeft = this.scrollLeft - dx;
      container.scrollTop = this.scrollTop - dy;
    },

    stopPan() {
      this.isPanning = false;
      this.$refs.mermaidContainer.style.cursor = "grab";
    },
    zoomIn() {
      this.zoomLevel += 0.1; // Remove max zoom limit
      this.applyZoom();
    },
    zoomOut() {
      this.zoomLevel = Math.max(this.zoomLevel - 0.1, 0.5); // Keep min zoom at 0.5x
      this.applyZoom();
    },
    resetZoom() {
      this.zoomLevel = 1; // Reset zoom to default
      this.applyZoom();
    },
    applyZoom() {
      const svgElement = this.$refs.mermaidContainer.querySelector("svg");
      if (svgElement) {
        svgElement.style.transform = `scale(${this.zoomLevel})`;
        svgElement.style.transformOrigin = "top left"; // Always zoom from the top-left corner
      }
    },
    downloadDiagram() {
      const svgElement = this.$refs.mermaidContainer.querySelector("svg");
      if (!svgElement) {
        console.error("No SVG element found to download.");
        return;
      }

      const serializer = new XMLSerializer();
      const svgContent = serializer.serializeToString(svgElement);
      const blob = new Blob([svgContent], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      // Use the title prop as the file name, fallback to 'mermaid-diagram' if not set
      const safeTitle = (this.title || "mermaid-diagram").replace(
        /[\\/:*?"<>|]/g,
        "_"
      );
      link.download = `${safeTitle}.svg`;
      link.click();

      URL.revokeObjectURL(url); // Clean up the URL object
    },
    close() {
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
/* Backdrop */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(6px);
  display: flex;
  justify-content: center;
  align-items: center;
  animation: fadeIn 0.25s ease-out;
  z-index: 9999;
}

/* Modal container */
.modal-content {
  background: var(--surface, #1e1e2e);
  padding: 24px;
  border-radius: 16px;
  width: 78vw;
  height: 78vh;
  max-width: 92vw;
  max-height: 92vh;

  display: flex;
  flex-direction: column;

  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.45);
  border: 1px solid var(--border-color, #2c2c3a);

  animation: modalPop 0.25s ease-out;
}

/* Title */
.popup-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-primary, #fff);
}

/* Header row */
.header-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  position: relative;
}

/* Button group (right side) */
.header-controls > div {
  display: flex;
  gap: 10px;
}

/* Icon buttons */
.btn-sm {
  font-size: 1.05rem;
  background: transparent;
  border: none;
  color: var(--primary, #8a8cff);
  cursor: pointer;

  padding: 6px;
  border-radius: 8px;

  transition: transform 0.2s ease, background-color 0.2s ease, color 0.2s ease;
}

.btn-sm:hover {
  background: rgba(138, 140, 255, 0.12);
  color: var(--text-primary, #fff);
  transform: scale(1.18);
}

.btn-sm:active {
  transform: scale(0.9);
}

/* Divider */
.custom-hr {
  border: none;
  border-top: 1px solid var(--border-color, #343445);
  margin: 2px -24px 14px -24px;
}

/* Mermaid container */
.mermaid {
  flex: 1;
  width: 100%;
  border-radius: 12px;
  padding: 12px;
  overflow: auto;
  background:#fff;
  border: 1px solid var(--border-color, #343445);
  cursor: grab;
}

/* .edge-thickness-thick {
  stroke: #fff !important;
  fill: #fff !important;
} */

.lightTheme {
  --surface: #ffffff;
  --surface-hover: #f7f7f7;
  --border-color: #dcdcdc;
  --text-primary: #111;
  background: var(--surface);
}

.lightTheme.mermaid {
  background: var(--surface-hover, #f7f7f7);
  border-color: var(--border-color, #dcdcdc);
}



/* Scrollbar */
.mermaid::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.mermaid::-webkit-scrollbar-thumb {
  background-color: var(--primary, #8a8cff);
}

.mermaid::-webkit-scrollbar-thumb:hover {
  background-color: #b0b0ff;
}

.mermaid::-webkit-scrollbar-track {
  background-color: var(--surface, #1e1e2e);
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes modalPop {
  0% {
    transform: scale(0.92);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
